/**
 * Calling Hours Page
 *
 * Full management UI for calling hour windows grouped by jurisdiction,
 * with 24h timeline visualization and CRUD operations.
 */
import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, Badge, Button, Input, cn } from '@respectly/ui';
import { Switch } from '@respectly/ui';
import {
  Clock,
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  BookOpen,
  Globe,
} from 'lucide-react';
import type { CallingHourWindow, Jurisdiction } from '@respectly/shared-types';
import { usePolicyStore } from '../../stores/usePolicyStore';
import { CallingHoursTimeline } from '../../components/policy/CallingHoursTimeline';
import { CallingHoursForm } from '../../components/policy/CallingHoursForm';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const cardListVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const cardItemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

// ============================================
// Helpers
// ============================================

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatTime(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return minute === 0
    ? `${displayHour}:00 ${period}`
    : `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
}

function formatDays(daysOfWeek: number[]): string {
  if (daysOfWeek.length === 7) return 'Mon–Sun';
  if (
    daysOfWeek.length === 5 &&
    [1, 2, 3, 4, 5].every((d) => daysOfWeek.includes(d))
  ) {
    return 'Mon–Fri';
  }
  if (
    daysOfWeek.length === 6 &&
    !daysOfWeek.includes(0)
  ) {
    return 'Mon–Sat';
  }
  if (
    daysOfWeek.length === 2 &&
    daysOfWeek.includes(0) &&
    daysOfWeek.includes(6)
  ) {
    return 'Weekends';
  }
  return daysOfWeek.map((d) => DAY_LABELS[d]).join(', ');
}

interface JurisdictionGroup {
  code: string;
  name: string;
  windows: CallingHourWindow[];
}

// ============================================
// Component
// ============================================

export function PolicyCallingHours() {
  const callingHours = usePolicyStore((s) => s.callingHours);
  const jurisdictions = usePolicyStore((s) => s.jurisdictions);
  const isLoadingCallingHours = usePolicyStore((s) => s.isLoadingCallingHours);
  const isLoadingJurisdictions = usePolicyStore((s) => s.isLoadingJurisdictions);
  const loadCallingHours = usePolicyStore((s) => s.loadCallingHours);
  const loadJurisdictions = usePolicyStore((s) => s.loadJurisdictions);
  const updateCallingHourWindow = usePolicyStore((s) => s.updateCallingHourWindow);
  const deleteCallingHourWindow = usePolicyStore((s) => s.deleteCallingHourWindow);

  const [formOpen, setFormOpen] = useState(false);
  const [editingWindow, setEditingWindow] = useState<CallingHourWindow | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadCallingHours();
    loadJurisdictions();
  }, [loadCallingHours, loadJurisdictions]);

  // Build jurisdiction lookup map
  const jurisdictionMap = useMemo(() => {
    const map = new Map<string, Jurisdiction>();
    jurisdictions.forEach((j) => map.set(j.code, j));
    return map;
  }, [jurisdictions]);

  // Group calling hours by jurisdiction
  const groups = useMemo((): JurisdictionGroup[] => {
    const map = new Map<string, CallingHourWindow[]>();
    callingHours.forEach((w) => {
      const existing = map.get(w.jurisdictionCode) ?? [];
      existing.push(w);
      map.set(w.jurisdictionCode, existing);
    });

    const result: JurisdictionGroup[] = [];
    map.forEach((windows, code) => {
      const j = jurisdictionMap.get(code);
      const name = j?.name ?? code;
      if (
        searchQuery &&
        !name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !code.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return;
      }
      result.push({ code, name, windows });
    });
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [callingHours, jurisdictionMap, searchQuery]);

  const isLoading = isLoadingCallingHours || isLoadingJurisdictions;

  const handleEdit = (w: CallingHourWindow) => {
    setEditingWindow(w);
    setFormOpen(true);
  };

  const handleDelete = async (w: CallingHourWindow) => {
    if (!window.confirm(`Delete calling hour window "${w.name}"?`)) return;
    await deleteCallingHourWindow(w.id);
  };

  const handleToggle = async (w: CallingHourWindow) => {
    await updateCallingHourWindow(w.id, { isActive: !w.isActive });
  };

  const handleAdd = () => {
    setEditingWindow(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingWindow(null);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center gap-4">
          <Link
            to="/settings/policy"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/40 dark:to-amber-800/40 flex items-center justify-center">
            <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Calling Hours
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Configure permitted calling and messaging windows by jurisdiction
            </p>
          </div>
        </div>
      </motion.div>

      {/* Action Bar */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-4 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center gap-3">
          <Button onClick={handleAdd} size="sm">
            <Plus className="w-4 h-4 mr-1.5" />
            Add Window
          </Button>
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter by jurisdiction..."
              className="pl-9"
            />
          </div>
          <div className="ml-auto text-sm text-slate-500 dark:text-slate-400">
            {callingHours.length} window{callingHours.length !== 1 ? 's' : ''} across{' '}
            {groups.length} jurisdiction{groups.length !== 1 ? 's' : ''}
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : groups.length === 0 ? (
          <motion.div variants={itemVariants} className="flex items-center justify-center h-64">
            <Card className="border-dashed border-2 border-slate-200 dark:border-slate-700 max-w-md w-full">
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                  No calling hour windows
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  {searchQuery
                    ? 'No windows match your search. Try a different filter.'
                    : 'Add your first calling hour window to get started.'}
                </p>
                {!searchQuery && (
                  <Button size="sm" onClick={handleAdd}>
                    <Plus className="w-4 h-4 mr-1.5" />
                    Add Window
                  </Button>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div variants={cardListVariants} initial="hidden" animate="visible" className="space-y-8">
            {groups.map((group) => (
              <motion.div key={group.code} variants={cardItemVariants}>
                {/* Jurisdiction section header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <Globe className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    {group.name}
                  </h2>
                  <Badge variant="secondary" className="text-[10px] tabular-nums">
                    {group.windows.length}
                  </Badge>
                </div>

                {/* Timeline */}
                <div className="mb-4 px-2">
                  <CallingHoursTimeline
                    windows={group.windows}
                    jurisdictionName={group.name}
                  />
                </div>

                {/* Window cards */}
                <div className="space-y-3">
                  {group.windows.map((w) => (
                    <Card
                      key={w.id}
                      className={cn(
                        'border-slate-200/60 dark:border-slate-700/60',
                        !w.isActive && 'opacity-50'
                      )}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          {/* Left content */}
                          <div className="flex-1 space-y-2">
                            {/* Name + ID */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-slate-900 dark:text-white">
                                {w.name}
                              </span>
                              <Badge variant="outline" className="font-mono text-[10px]">
                                {w.windowId}
                              </Badge>
                            </div>

                            {/* Badges: Channel, Purpose */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {w.channel && (
                                <Badge variant="secondary" className="text-[10px]">
                                  {w.channel}
                                </Badge>
                              )}
                              {w.messagePurpose && (
                                <Badge
                                  variant={w.messagePurpose === 'MARKETING' ? 'info' : 'success'}
                                  className="text-[10px]"
                                >
                                  {w.messagePurpose}
                                </Badge>
                              )}
                            </div>

                            {/* Time + Days */}
                            <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
                              <span className="font-medium">
                                {formatTime(w.startHour, w.startMinute)} –{' '}
                                {formatTime(w.endHour, w.endMinute)}
                              </span>
                              <span className="text-slate-400">|</span>
                              <span>{formatDays(w.daysOfWeek)}</span>
                            </div>

                            {/* Timezone + Source */}
                            <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                              <span>TZ: {w.timezone}</span>
                              {w.sourceRef && (
                                <span className="flex items-center gap-1">
                                  <BookOpen className="w-3 h-3" />
                                  {w.sourceRef}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Right controls */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Switch
                              checked={w.isActive}
                              onCheckedChange={() => handleToggle(w)}
                              className="scale-75"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(w)}
                              className="h-8 w-8 p-0"
                            >
                              <Pencil className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(w)}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Form modal */}
      <CallingHoursForm
        isOpen={formOpen}
        onClose={handleFormClose}
        window={editingWindow}
        jurisdictions={jurisdictions}
      />
    </motion.div>
  );
}
